import { useEffect, useState } from "react";
import axios from "axios";
import AddCredit from "./AddCredit";
import { formatDecimal } from "../../../utils/decimalFormat";

const CreditPanel = ({ id, currency, setAlertConfig, fetchMerchantDetails, creditRefreshKey }) => {
    const [overview, setOverview] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const token = localStorage.getItem("token");

    const fetchOverview = () => {
        axios
            .get(`${process.env.REACT_APP_API_ENDPOINT}merchant/${id}/credit-overview`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((res) => res.data.code === 1 && setOverview(res.data.data))
            .catch(console.error);
    };

    useEffect(() => {
        fetchOverview();
        // eslint-disable-next-line
    }, [id, creditRefreshKey]);

    if (!overview) return null;

    const { creditFrozen, currentCycle, history, pastCycles } = overview;
    const cycleLabel = new Date(currentCycle.cycleStart).toLocaleDateString(undefined, {
        month: "long", year: "numeric",
    });
    const coveragePct = currentCycle.creditGivenInt > 0
        ? Math.min(100, Math.round((currentCycle.toppedUpInt / currentCycle.creditGivenInt) * 100))
        : 100;

    return (
        <div className="row g-5 g-lg-10 mt-4">
            <div className="col p-0 mb-xl-10">
                <h1 className="d-flex text-dark fw-bold m-0 fs-3 mb-5">Credit</h1>

                {creditFrozen && (
                    <div className="alert alert-danger d-flex align-items-center mb-4">
                        <i className="bi bi-snow2 fs-2 me-3" />
                        <div>
                            <div className="fw-bold">Merchant Frozen</div>
                            <div>Unpaid credit balance from a previous cycle. Ordering resumes automatically once enough topup clears it.</div>
                        </div>
                    </div>
                )}

                {/* This Cycle */}
                <div className="card mb-4 border-0 shadow-sm">
                    <div className="card-header border-0 bg-transparent pt-6 pb-0">
                        <div className="d-flex justify-content-between align-items-center w-100">
                            <div>
                                <div className="text-muted fs-7 text-uppercase fw-semibold">Current Billing Cycle</div>
                                <h3 className="fw-bold mb-0">{cycleLabel}</h3>
                            </div>
                            <button className="btn btn-primary d-flex align-items-center gap-2" onClick={() => setShowAdd(true)}>
                                <i className="bi bi-plus-circle" /> Add Credit
                            </button>
                        </div>
                    </div>
                    <div className="card-body pt-6">
                        <div className="row g-4">
                            <div className="col-md-4">
                                <div className="p-5 rounded bg-light-primary h-100">
                                    <div className="text-primary fw-semibold fs-7 text-uppercase mb-2">Credit Given</div>
                                    <div className="fs-2x fw-bolder text-gray-900">
                                        {formatDecimal(currentCycle.creditGivenInt)}
                                        <span className="fs-6 fw-normal text-muted ms-1">{currency}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="p-5 rounded bg-light-success h-100">
                                    <div className="text-success fw-semibold fs-7 text-uppercase mb-2">Topped Up</div>
                                    <div className="fs-2x fw-bolder text-gray-900">
                                        {formatDecimal(currentCycle.toppedUpInt)}
                                        <span className="fs-6 fw-normal text-muted ms-1">{currency}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className={`p-5 rounded h-100 ${currentCycle.netOutstandingInt > 0 ? "bg-light-danger" : "bg-light-success"}`}>
                                    <div className={`fw-semibold fs-7 text-uppercase mb-2 ${currentCycle.netOutstandingInt > 0 ? "text-danger" : "text-success"}`}>
                                        Net Outstanding
                                    </div>
                                    <div className="fs-2x fw-bolder text-gray-900">
                                        {formatDecimal(currentCycle.netOutstandingInt)}
                                        <span className="fs-6 fw-normal text-muted ms-1">{currency}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {currentCycle.creditGivenInt > 0 && (
                            <div className="mt-6">
                                <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted fs-7">Coverage this cycle</span>
                                    <span className="fw-semibold fs-7">{coveragePct}%</span>
                                </div>
                                <div className="progress" style={{ height: "8px" }}>
                                    <div
                                        className={`progress-bar ${coveragePct >= 100 ? "bg-success" : "bg-warning"}`}
                                        style={{ width: `${coveragePct}%` }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Credit History */}
                {history?.length > 0 && (
                    <div className="card mb-4 border-0 shadow-sm">
                        <div className="card-header border-0 pt-6">
                            <h3 className="fw-bold mb-0">Credit History</h3>
                        </div>
                        <div className="card-body pt-2">
                            <div className="table-responsive">
                                <table className="table table-row-dashed align-middle gy-3">
                                    <thead>
                                        <tr className="text-muted text-uppercase fs-8">
                                            <th className="min-w-150px">Date</th>
                                            <th className="min-w-100px">Amount</th>
                                            <th>Note</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map((g) => (
                                            <tr key={g._id}>
                                                <td className="text-muted">{new Date(g.createdAt).toLocaleString()}</td>
                                                <td>
                                                    <span className="badge badge-light-primary fs-7 fw-bold">
                                                        +{formatDecimal(g.amountInt)} {currency}
                                                    </span>
                                                </td>
                                                <td className="text-muted">{g.note || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Past Cycles */}
                {pastCycles?.length > 0 && (
                    <div className="card border-0 shadow-sm">
                        <div className="card-header border-0 pt-6">
                            <h3 className="fw-bold mb-0">Past Cycles</h3>
                        </div>
                        <div className="card-body pt-2">
                            <div className="table-responsive">
                                <table className="table table-row-dashed align-middle gy-3">
                                    <thead>
                                        <tr className="text-muted text-uppercase fs-8">
                                            <th>Month</th>
                                            <th>Given</th>
                                            <th>Topped Up</th>
                                            <th>Net</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pastCycles.map((c) => (
                                            <tr key={c._id}>
                                                <td className="fw-semibold">
                                                    {new Date(c.cycleStart).toLocaleDateString(undefined, { month: "short", year: "numeric" })}
                                                </td>
                                                <td>{formatDecimal(c.creditGivenInt)} {currency}</td>
                                                <td>{formatDecimal(c.toppedUpInt)} {currency}</td>
                                                <td className={c.netOutstandingInt > 0 ? "text-danger fw-semibold" : ""}>
                                                    {formatDecimal(c.netOutstandingInt)} {currency}
                                                </td>
                                                <td>
                                                    <span className={`badge ${c.status === "settled" ? "badge-light-success"
                                                            : c.status === "cleared" ? "badge-light-info"
                                                                : "badge-light-danger"
                                                        }`}>
                                                        {c.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showAdd && (
                <AddCredit
                    id={id}
                    onClose={() => setShowAdd(false)}
                    setAlertConfig={setAlertConfig}
                    onSuccess={() => {
                        fetchOverview();
                        fetchMerchantDetails();
                    }}
                />
            )}
        </div>
    );
};

export default CreditPanel;